/**
 * The allowances, with the ones that went over shown and the rest folded.
 *
 * DESIGN.md P3: "6 allowance meters → show the ones **over**; '3 others within
 * allowance →'." That is the whole of this component's opinion, and it is why it
 * exists rather than each page mapping `AllowanceStatusView[]` to `Meter`: the
 * dashboard, the report and the billing page all draw the same six rows and only
 * one of them should decide which are worth a meter.
 *
 * P4 holds too: a meter is ochre past the allowance and nothing else here is. A
 * client who is inside every allowance gets one muted line and no bars at all —
 * "if everything is flagged, nothing is flagged", and the same is true of nothing.
 *
 * **It computes no money.** `over` is the read model's own field
 * (`retainers.allowanceStatus`, computed from deliverables and stored nowhere), and
 * the price of going over is `billing.html`'s to state — this prints quantities.
 */

import { CONTENT_TYPE_CODES, CONTENT_TYPE_NOUNS, type AllowanceStatusView } from '@fareast/shared'

import { Fold } from '../portal/Disclosure.js'
import { Meter } from '../portal/Meter.js'
import { RegionCols } from '../portal/Region.js'
import { Muted, Small } from '../portal/Text.js'
import { useFormat, type Formatter } from '../../format/useFormat.js'

export type AllowanceMetersProps = {
  readonly allowances: readonly AllowanceStatusView[]
  /**
   * Draw every meter rather than folding the ones within allowance. The report
   * document does — it is a record of a month rather than a page you scan.
   */
  readonly showAll?: boolean
  /** The line under the meters when there is nothing over. */
  readonly emptyLine?: string
}

/**
 * The units an allowance can be spent a fraction of.
 *
 * Odoo management is sold by the hour and `client/dashboard.html` prints `6.0 / 6`
 * for a month that used exactly six of them — the decimal is a fact about the unit,
 * not about this month's number, and dropping it whenever the total happens to land
 * whole would make the same allowance read two different ways in two months.
 * `pricing.allowanceExcess` takes a fractional `used` and a whole `included` for the
 * same reason (`CLAUDE.md` §Gotchas); this is the rendering half of it.
 */
const FRACTIONAL_UNITS: readonly string[] = ['hour']

function fractionDigitsFor(one: AllowanceStatusView): number | undefined {
  return FRACTIONAL_UNITS.includes(one.unit) || !Number.isInteger(one.used) ? 1 : undefined
}

/**
 * `14 reels, 8 carousels, 6 statics` — what a credit allowance was spent on.
 *
 * The nouns come from `CONTENT_TYPE_NOUNS` and the number picks the form, because
 * appending an `s` turns `story` into "storys". `AllowanceStatusView.credits` is
 * keyed by `ContentTypeCode`, so the map is total over it and the order is the
 * enum's — reels before carousels before statics, as `PRICING.md` lists them and as
 * every mockup prints them.
 */
function creditsFoot(one: AllowanceStatusView, format: Formatter): string | null {
  if (one.credits === null) return null
  const credits = one.credits
  const parts = CONTENT_TYPE_CODES.filter((code) => credits[code] > 0).map((code) =>
    format.qty(credits[code], CONTENT_TYPE_NOUNS[code]),
  )
  return parts.length === 0 ? null : parts.join(', ')
}

export function AllowanceMeters({ allowances, showAll, emptyLine }: AllowanceMetersProps) {
  const format = useFormat()

  const over = allowances.filter((one) => one.over > 0)
  const within = allowances.filter((one) => one.over === 0)
  const shown = showAll === true ? allowances : over
  const folded = showAll === true ? [] : within

  const meter = (one: AllowanceStatusView) => {
    /*
     * The foot, in the mockup's own two shapes.
     *
     * A credit allowance names what the credits went on and then how many credits
     * are over — "14 reels, 8 carousels, 6 statics · 46 credits over". Everything
     * else names what is included and then how much is past it — "4 included · 1
     * over". `client/dashboard.html` §Allowances draws both.
     */
    const credits = creditsFoot(one, format)
    const left = credits ?? `${format.number(one.included)} included`
    const over =
      credits === null
        ? `${format.number(one.over)} over`
        : `${format.qty(one.over, { one: one.unit, other: `${one.unit}s` })} over`
    const foot = one.over > 0 ? `${left} · ${over}` : left

    return (
      <Meter
        key={one.service}
        label={one.service_name}
        used={one.used}
        included={one.included}
        foot={foot}
        fractionDigits={fractionDigitsFor(one)}
      />
    )
  }

  return (
    <>
      {shown.length > 0 ? <RegionCols>{shown.map(meter)}</RegionCols> : null}

      {shown.length === 0 ? (
        <Small>
          <Muted>
            {emptyLine ??
              'Everything is inside what your package includes this month. Nothing to approve.'}
          </Muted>
        </Small>
      ) : null}

      {folded.length > 0 ? (
        <Fold
          summary={`${format.number(folded.length)} ${
            folded.length === 1 ? 'other' : 'others'
          } within allowance`}
        >
          <RegionCols>{folded.map(meter)}</RegionCols>
        </Fold>
      ) : null}
    </>
  )
}
